// The overview's four headline tiles: responses (with count delta vs the
// previous window), normalized average score (with delta in percentage
// points), completion rate, and surveys active-in-range vs total. Pure
// presentation over overview-mapping's null-safe helpers — a tile reads "—"
// rather than a misleading 0 whenever the window has nothing to report.
// The score tile is labelled as normalized, with a hint, because it is a
// 0–100% share of each survey's own scale — a different unit from the
// results page's "Average score", which is the raw mean on one survey's
// scale — and the two must not read as the same number.

import type { WorkspaceOverview } from "@/api/types";
import { DeltaLine, NoEarlierData, StatTile, TILE_VALUE } from "@/features/dashboard/stat-tile";
import { completionRate, responsesDelta, scorePctDelta } from "./overview-mapping";

function formatPercent(value: number | null): string {
    return value === null ? "—" : `${Math.round(value * 100)}%`;
}

export function OverviewTiles({ overview, allTime }: { overview: WorkspaceOverview; allTime: boolean }) {
    const completion = completionRate(overview.current);
    // Nothing to compare with: All time, or a previous window nobody answered in.
    const comparable = !allTime && overview.previous.responses > 0;
    const pctDelta = scorePctDelta(overview);
    return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatTile label="Responses">
                <p data-testid="tile-responses" className={TILE_VALUE}>
                    {overview.current.responses}
                </p>
                {comparable ? (
                    <DeltaLine
                        testId="tile-delta-count"
                        delta={responsesDelta(overview)}
                        unit="vs previous"
                        digits={0}
                    />
                ) : (
                    <NoEarlierData />
                )}
            </StatTile>
            <StatTile label="Normalized score" hint="0–100%, comparable across surveys with different scales">
                <p data-testid="tile-average-score" className={TILE_VALUE}>
                    {formatPercent(overview.current.averageScorePct)}
                </p>
                {comparable && pctDelta !== null ? (
                    <DeltaLine
                        testId="tile-delta-score"
                        delta={Math.round(pctDelta * 100)}
                        unit="pts vs previous"
                        digits={0}
                    />
                ) : (
                    <NoEarlierData />
                )}
            </StatTile>
            <StatTile label="Completion">
                <p data-testid="tile-completion" className={TILE_VALUE}>
                    {formatPercent(completion)}
                </p>
            </StatTile>
            <StatTile label="Surveys with responses">
                <p data-testid="tile-surveys" className={TILE_VALUE}>
                    {overview.surveys.withResponses}
                    <span className="text-base font-normal text-muted-foreground"> / {overview.surveys.total}</span>
                </p>
            </StatTile>
        </div>
    );
}
